import { Badge, baseVariables, toRem } from '@marriott/mi-ui-library';
import styled, { css } from 'styled-components';
import { BreakPointKeyProps } from './ProductDetailsCard.types';

const borderRadius = 16;
const aspectRatios = {
  tall: '2 / 3',
  wide: '16 / 9',
  square: '1 / 1',
};

// Reusable function to pass dynamic value of breakpoint to tall variation
export const responsiveMediaQuery = (breakPointKey: BreakPointKeyProps) => {
  return breakPointKey === 'viewportL' ? baseVariables.mediaQuery.lg : baseVariables.mediaQuery.xl;
};

export const StyledProductDetailsCard = styled.div<{
  cardVariation: string;
  $isExpanded: boolean;
  breakPointKey: BreakPointKeyProps;
}>`
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border-radius: ${borderRadius}px;
  background: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: ${({ cardVariation }) => (cardVariation === 'wide' ? 'row' : 'column')};
  width: 100%;
  transition: box-shadow ${baseVariables.animations.durationExtended};
  position: relative;
  aspect-ratio: ${aspectRatios.square};
  height: auto;

  &:focus-within,
  &:focus-visible {
    outline: 2px solid -webkit-focus-ring-color;
  }

  ${({ $isExpanded }) =>
    $isExpanded
      ? css`
          &:focus-within {
            outline: none;
          }
        `
      : css`
          &:focus-within:not(:has(.pdc-card:focus)) {
            outline: none;
          }
        `}

  &.pdc-tall {
    @media ${props => responsiveMediaQuery(props.breakPointKey)} {
      aspect-ratio: ${aspectRatios.tall};
    }
  }
  &.pdc-wide {
    width: 100%;
    @media ${props => responsiveMediaQuery(props.breakPointKey)} {
      aspect-ratio: ${aspectRatios.wide};
    }
  }

  .pdc-card {
    position: relative;
    z-index: 1;
    width: 100%;
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: ${borderRadius}px;
      background: linear-gradient(0deg, rgba(28, 28, 28, 0.8) 0%, rgba(28, 28, 28, 0.8) 100%);
      opacity: 0;
      transition: opacity ${baseVariables.animations.durationStandard};
      z-index: 2;
      pointer-events: none;
    }

    @media ${props => responsiveMediaQuery(props.breakPointKey)} {
      &:hover::after,
      &:focus-within::after,
      &:focus-visible::after {
        opacity: 0.8;
      }
      &:hover .pdc-hovercontent,
      &:focus-visible .pdc-hovercontent,
      &:focus-within .pdc-hovercontent {
        display: block;
      }

      &:hover .pdc-brand-logo::before,
      &:focus-visible .pdc-brand-logo::before,
      &:focus-within .pdc-brand-logo::before,
      &:hover .pdc-content::before,
      &:focus-visible .pdc-content::before,
      &:focus-within .pdc-content::before {
        transition: opacity 200ms ease-in 50ms;
        padding-bottom: 0;
        opacity: 0;
      }
    }
    &.is-expanded {
      &::after {
        opacity: 0.8;
      }
      .pdc-hovercontent {
        display: block;
      }
      .pdc-brand-logo::before,
      .pdc-content::before {
        transition: opacity 200ms ease-in 50ms;
        padding-bottom: 0;
        opacity: 0;
      }
    }
  }

  // Image styles
  picture {
    width: 100%;
    aspect-ratio: inherit;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    aspect-ratio: ${aspectRatios.square};
  }
  &.pdc-wide picture {
    @media ${props => responsiveMediaQuery(props.breakPointKey)} {
      aspect-ratio: ${aspectRatios.wide};
    }
  }
  &.pdc-tall picture {
    @media ${props => responsiveMediaQuery(props.breakPointKey)} {
      aspect-ratio: ${aspectRatios.tall};
    }
  }
  .pdc-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: ${borderRadius}px ${borderRadius}px 0 0;
    display: block;
  }

  // Content styles
  .pdc-hovercontent {
    display: none;
  }
  .pdc-content {
    width: 100%;
    position: absolute;
    inset: 0;
    z-index: 3;
    display: flex;
    flex-direction: column;
    border-radius: ${borderRadius}px;
    pointer-events: none;
    padding: 24px;
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: ${borderRadius}px;
      height: 164px;
      z-index: 1;
      opacity: 1;
      transition: opacity 200ms ease-in 100ms;
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0.62) 0.08%,
        rgba(0, 0, 0, 0.54) 62.91%,
        rgba(0, 0, 0, 0) 95.83%
      );
    }
  }
  .pdc-content-top {
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;
    z-index: 2;
  }
  .pdc-content-bottom {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-top: auto;
  }

  // Brand logo styles
  .pdc-brand-logo {
    &::before {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      width: 160%;
      height: 80%;
      pointer-events: none;
      z-index: 1;
      background: radial-gradient(109.43% 141.42% at 0% 100%, rgba(0, 0, 0, 0.54) 20%, rgba(0, 0, 0, 0) 40%);
    }
    > span {
      position: relative;
      z-index: 2;
    }
  }

  // Mobile toggle styles
  .pdc-mobile-toggle {
    position: absolute;
    bottom: 24px;
    right: 24px;

    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    background: ${baseVariables.color.base20};
    border-radius: 50%;
    cursor: pointer;
    pointer-events: auto;
    z-index: 5;
    height: 28px;
    width: 28px;

    &:focus {
      outline: 2px solid -webkit-focus-ring-color;
    }

    @media ${props => responsiveMediaQuery(props.breakPointKey)} {
      display: none;
    }
    [class*='icon-'] {
      transition: transform ${baseVariables.animations.durationStandard} ease;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      line-height: 18px;
      position: relative;
      &::before {
        font-size: 18px;
        line-height: 18px;
        position: relative;
      }
    }
    .icon-clear {
      bottom: 1px;
    }
  }
`;

export const StyledEyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  line-height: 12px;
`;

export const StyledTitleRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  justify-content: space-between;
  .icon-arrow-right {
    height: 24px;
    width: 24px;
    font-size: 24px;
    margin-bottom: 4px;
  }
`;

export const StyledTags = styled.ul`
  display: flex;
  gap: 4px;
  padding: 0;
  list-style: none;
  margin-bottom: 0;

  & li {
    color: ${baseVariables.color.base20};
    display: flex;
    font-size: 12px;
    flex-direction: column;
    justify-content: center;
    padding: 0 8px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.54);
  }
`;

export const TagsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  height: ${toRem(24)};
  gap: 0.25rem;
  margin-bottom: 0;
`;

export const StyledBadge = styled(Badge)`
  height: 100%;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  font-size: ${toRem(12)};
  font-weight: 700;

  ${({ badgeIcon }) =>
    badgeIcon &&
    css`
      .${badgeIcon} {
        height: ${toRem(15)};
        width: ${toRem(15)};
        position: relative;

        &:before {
          height: ${toRem(15)};
          width: ${toRem(15)};
          position: absolute;
          transform: translateY(50%);
          top: -8px; // works together with transform based on icon height
          padding-top: 2px; // icon shows slightly above bounding box
        }
      }
    `}
`;
